import React, { useEffect, useRef, useState } from "react";
import PortaledModal from "../../../../components/Modals/PortaledModal";
import FastCar from "../../../../icons/Fast-Car-White.svg";
import { translate } from "../../../../utils/translator";
import { MainTracker } from "../../../../PixelTrackers/MainTracker";
import { useCartContext } from "../../../../customHooks/useCart";
import { useCustomer } from "../../../../customHooks/useCustomer";
import { useShippingPageContext } from "../providers/ShippingPageProvider";
import { resolveExpressShippingTiers } from "../utils/expressShippingTiers";
import { isDhlNextDayDisplayed, resolveDhlTitleKey } from "../utils/dhlTitle";
import { SELF_PICKUP, PRODUCT_TYPE_PRESCRIPTION_DRUG, SELF_PICKUP_PAYMENT_MODELS } from "../constants";
import { EXPRESS_SHIPPING, PICKUP, SHIPPING } from "../../../ChooseTreatmentVariants/FreeDosageChooseTreatmentV2/constants";

const EXPRESS_ETA_OPTIONS = ["same_day", "next_day"];

// Renders a translation with its {placeholders} in bold, as in Figma.
const withBoldValues = (text: string, values: Record<string, string>) =>
  text.split(/(\{\w+\})/).map((part, index) => {
    const key = part.slice(1, -1);
    return part.startsWith("{") && key in values ? <strong key={index}>{values[key]}</strong> : part;
  });

const UnavailableDeliveryMethodPopup: React.FC = () => {
  const { language, country, contentType, selectedFormID, isUrbifyFullyRolledOut, availableExpressTiers, deliveryUnavailableOptions, setDeliveryUnavailableOptions, handleDeliveryOptionChange, setIsPickupSheetOpen, setRxSelfPickupServiceOption, setSelectedPickupPharmacyCode } = useShippingPageContext();
  const { getCart } = useCartContext();
  const { customerState } = useCustomer();
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const timesShown = useRef(0);

  const cart = getCart();
  const isOpen = deliveryUnavailableOptions.length > 0;
  const rxProduct = cart.products.find((product) => product.type === PRODUCT_TYPE_PRESCRIPTION_DRUG);
  const productItemID = rxProduct?.product_item_id ?? null;
  const isExpressSelected = cart.selected_service_option === "express";
  const isSelfPickupSelected = cart.selected_service_option === SELF_PICKUP;
  const selectedShipment = isExpressSelected ? EXPRESS_SHIPPING : isSelfPickupSelected ? PICKUP : SHIPPING;
  const getTrackingTab = (option: string) => option === SELF_PICKUP ? PICKUP : option === "regular" ? SHIPPING : EXPRESS_SHIPPING;

  useEffect(() => {
    if (!isOpen) return;
    setSelectedOption(deliveryUnavailableOptions[0]);
    MainTracker.track("custom", "Delivery Unavailable Popup Shown", {
      selected_shipment: selectedShipment,
      available_shipments: deliveryUnavailableOptions.map(getTrackingTab),
      available_count: deliveryUnavailableOptions.length,
      product_item_id: productItemID,
      is_reopen: timesShown.current > 0,
    });
    timesShown.current += 1;
  }, [isOpen]);

  if (!isOpen || !selectedOption) return null;

  const t = (key: string) => translate(language, "shipping", `DeliveryUnavailablePopup/${key}`) as string | undefined;

  const { tiersToShow } = resolveExpressShippingTiers(availableExpressTiers, { ignoreAbTestGate: contentType !== "cannabis" && isUrbifyFullyRolledOut });
  const dhlTitleKey = resolveDhlTitleKey({
    country,
    isPreOrderCart: cart.products?.some((product: any) => product.is_pre_orderable) ?? false,
    contentType,
    isNextDayDisplayed: isDhlNextDayDisplayed({ country, isPersonalAddress: selectedFormID === "delivery-address-form", tiersToShow }),
  });
  const getOptionTitle = (option: string) => {
    if (option === SELF_PICKUP) return translate(language, "shipping", "RxSelfPickup/option_row_title") as string;
    if (option === "regular") return translate(language, "shipping", dhlTitleKey) as string;
    return translate(language, "shipping", `serviceOptions/service_option_${option}_title`) as string;
  };
  const getOptionEta = (option: string) => EXPRESS_ETA_OPTIONS.includes(option) ? t(`option_line_${option}`) : undefined;
  const selectedShipmentTitle = isExpressSelected
    ? (cart.selected_express_tier ? getOptionTitle(cart.selected_express_tier) : translate(language, "shipping", "serviceOptions/service_option_3_title") as string)
    : getOptionTitle(isSelfPickupSelected ? SELF_PICKUP : "regular");
  const hasSingleOption = deliveryUnavailableOptions.length === 1;
  const ctaLabel = (t("switch_to") ?? "").replace("{shipment}", getOptionTitle(selectedOption));

  const handleClose = () => {
    MainTracker.track("custom", "Delivery Unavailable Popup Closed", {
      selected_shipment: selectedShipment,
      available_count: deliveryUnavailableOptions.length,
      product_item_id: productItemID,
    });
    setSelectedOption(null);
    setDeliveryUnavailableOptions([]);
  };

  const handleOptionSelect = (option: string) => {
    if (option === selectedOption) return;
    MainTracker.track("custom", "Delivery Unavailable Popup Option Selected", {
      previous_option: getTrackingTab(selectedOption!),
      new_option: getTrackingTab(option),
      product_item_id: productItemID,
    });
    setSelectedOption(option);
  };

  const handleSwitch = async () => {
    const isSelfPickup = selectedOption === SELF_PICKUP;
    MainTracker.track("custom", "Delivery Unavailable Popup Switch Clicked", {
      selected_shipment: selectedShipment,
      new_shipment: getTrackingTab(selectedOption!),
      available_count: deliveryUnavailableOptions.length,
      product_item_id: productItemID,
      opens_pharmacy_popup: isSelfPickup,
    });
    MainTracker.track("custom", "Shipping_method_selected", {
      user_id: customerState?.customerID || null,
      previous_tab: selectedShipment,
      new_tab: getTrackingTab(selectedOption),
      cart_empty: cart.products.length === 0 ? "yes" : "no",
      source: "popup",
    });
    setSelectedOption(null);
    setDeliveryUnavailableOptions([]);

    // HL-13005: the method changes when a pharmacy is saved in the sheet; Pay Once because pickup is offered only with pharmacyCards.
    if (isSelfPickup) {
      setRxSelfPickupServiceOption(SELF_PICKUP_PAYMENT_MODELS.PAY_ONCE);
      setSelectedPickupPharmacyCode(null);
      setIsPickupSheetOpen(true);
      return;
    }
    if (selectedOption === "regular") {
      await handleDeliveryOptionChange("regular", undefined, { isManualSelection: true });
      return;
    }
    await handleDeliveryOptionChange("express", selectedOption, { isManualSelection: true });
  };

  return (
    <PortaledModal shouldShowModal={isOpen} hideModal={handleClose}>
      <div
        id="delivery-unavailable-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delivery-unavailable-popup-title"
        className="bg-white flex flex-col overflow-hidden font-poppins rounded-[12px] w-[min(460px,calc(100vw-32px))] max-h-[90vh] animate-fade-in-up"
      >
        <div className="bg-[#0A9281] text-white flex items-center justify-between gap-[8px] px-[16px] py-[12px] shrink-0">
          <div className="flex items-center gap-[8px] text-[16px] font-semibold">
            <img src={FastCar} alt="" className="w-[32px] h-[32px]" />
            <span id="delivery-unavailable-popup-title">{translate(language, "default", "split_delivery_popup/title")}</span>
          </div>
          <button
            id="delivery-unavailable-popup-close"
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="flex items-center justify-center shrink-0 w-[44px] h-[44px] -mr-[10px] bg-transparent border-none text-white text-[24px] leading-none cursor-pointer p-0"
          >
            ×
          </button>
        </div>

        <div className="flex flex-col gap-[12px] overflow-y-auto p-[16px]">
          <p id="delivery-unavailable-popup-body" className="m-0 text-[14px] leading-[21px] text-[#333]">
            {withBoldValues(t(hasSingleOption ? "body_single_option" : "body_multiple_options") ?? "", {
              selected_product: rxProduct?.title ?? "",
              selected_shipment: selectedShipmentTitle,
              available_shipment: getOptionTitle(deliveryUnavailableOptions[0]),
            })}
          </p>

          {hasSingleOption ? (
            <div className="border border-solid border-[#e5e7eb] rounded-[8px] px-[13px] pt-[12px] pb-[13px] text-[13px] leading-[18.2px] text-[#818181]">
              {[getOptionTitle(selectedOption), getOptionEta(selectedOption)].filter(Boolean).join(" · ")}
            </div>
          ) : (
            <div className="flex flex-col gap-[8px]" role="radiogroup" aria-labelledby="delivery-unavailable-popup-body">
              {deliveryUnavailableOptions.map((option) => {
                const isSelected = option === selectedOption;
                const eta = getOptionEta(option);
                return (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => handleOptionSelect(option)}
                    className={`flex items-start gap-[12px] w-full min-h-[44px] p-[12px] rounded-[8px] border border-solid text-left cursor-pointer ${
                      isSelected ? "bg-[#f2f9f7] border-[#52af8d]" : "bg-white border-[#e5e7eb]"
                    }`}
                  >
                    <span
                      className={`flex items-center justify-center shrink-0 w-[20px] h-[20px] rounded-full bg-white border border-solid ${
                        isSelected ? "border-[#52af8d]" : "border-[#767676]"
                      }`}
                    >
                      {isSelected && <span className="w-[12px] h-[12px] rounded-full bg-[#52af8d]" />}
                    </span>
                    <span className="flex flex-col min-w-0">
                      <span className="text-[14px] font-bold leading-[19.6px] text-[#333]">{getOptionTitle(option)}</span>
                      {eta && <span className="text-[13px] leading-[18.2px] text-[#818181]">{eta}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <button
            id="delivery-unavailable-popup-switch"
            type="button"
            onClick={handleSwitch}
            className="flex items-center justify-center h-[52px] px-[16px] rounded-[6px] bg-[#129181] border-none text-[14px] font-bold text-white cursor-pointer"
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </PortaledModal>
  );
};

export default UnavailableDeliveryMethodPopup;
